import { auth } from './firebase';
import {
  Business,
  CallLog,
  Lead,
  Appointment,
  AppointmentInput,
  Voice,
  CalendarSlot,
  BusinessMember,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Business API
export async function listBusinesses(): Promise<Business[]> {
  const data = await fetchWithAuth('/businesses');
  return data.businesses;
}

export async function createBusiness(business: Partial<Business>): Promise<Business> {
  return fetchWithAuth('/businesses', {
    method: 'POST',
    body: JSON.stringify(business),
  });
}

export async function getBusiness(businessId: string): Promise<Business> {
  return fetchWithAuth(`/businesses/${businessId}`);
}

export async function updateBusiness(
  businessId: string,
  updates: Partial<Business>
): Promise<Business> {
  return fetchWithAuth(`/businesses/${businessId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Calls API
export async function listCalls(
  businessId: string,
  limit?: number,
  startAfter?: string
): Promise<CallLog[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (startAfter) params.set('startAfter', startAfter);

  const data = await fetchWithAuth(
    `/businesses/${businessId}/calls?${params.toString()}`
  );
  return data.calls;
}

export async function getCall(businessId: string, callId: string): Promise<CallLog> {
  return fetchWithAuth(`/businesses/${businessId}/calls/${callId}`);
}

// Leads API
export async function listLeads(
  businessId: string,
  status?: string,
  limit?: number
): Promise<Lead[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit) params.set('limit', limit.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/leads?${params.toString()}`
  );
  return data.leads;
}

export async function updateLead(
  businessId: string,
  leadId: string,
  updates: Partial<Lead>
): Promise<Lead> {
  return fetchWithAuth(`/businesses/${businessId}/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Appointments API
export async function listAppointments(
  businessId: string,
  startDate?: string,
  endDate?: string,
  status?: string
): Promise<Appointment[]> {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (status) params.set('status', status);

  const data = await fetchWithAuth(
    `/businesses/${businessId}/appointments?${params.toString()}`
  );
  return data.appointments;
}

export async function createAppointment(
  businessId: string,
  appointment: AppointmentInput
): Promise<Appointment> {
  return fetchWithAuth(`/businesses/${businessId}/appointments`, {
    method: 'POST',
    body: JSON.stringify(appointment),
  });
}

export async function updateAppointment(
  businessId: string,
  appointmentId: string,
  updates: Partial<Appointment>
): Promise<Appointment> {
  return fetchWithAuth(`/businesses/${businessId}/appointments/${appointmentId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAppointment(
  businessId: string,
  appointmentId: string
): Promise<void> {
  await fetchWithAuth(`/businesses/${businessId}/appointments/${appointmentId}`, {
    method: 'DELETE',
  });
}

// Availability API
export async function getAvailability(
  businessId: string,
  date: string,
  duration?: number
): Promise<CalendarSlot[]> {
  const params = new URLSearchParams();
  params.set('date', date);
  if (duration) params.set('duration', duration.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/availability?${params.toString()}`
  );
  return data.slots;
}

// Voices API
export async function listVoices(): Promise<Voice[]> {
  const data = await fetchWithAuth('/voices');
  return data.voices;
}

// Cache API
export async function clearCache(businessId: string): Promise<number> {
  const data = await fetchWithAuth(`/businesses/${businessId}/clear-cache`, {
    method: 'POST',
  });
  return data.cleared;
}

// Members API
export async function listMembers(businessId: string): Promise<BusinessMember[]> {
  const data = await fetchWithAuth(`/businesses/${businessId}/members`);
  return data.members;
}

export async function addMember(
  businessId: string,
  email: string,
  role: string,
  name?: string
): Promise<BusinessMember> {
  return fetchWithAuth(`/businesses/${businessId}/members`, {
    method: 'POST',
    body: JSON.stringify({ email, role, name }),
  });
}

export async function removeMember(
  businessId: string,
  memberId: string
): Promise<void> {
  await fetchWithAuth(`/businesses/${businessId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// ==================== Admin/Agency API ====================

const ADMIN_API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || '/api'}/admin`;

async function fetchAdminWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${ADMIN_API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Agency API
export async function listAgencies(): Promise<{ agencies: any[]; memberships: any[] }> {
  return fetchAdminWithAuth('/agencies');
}

export async function createAgency(data: { name: string; slug?: string }): Promise<any> {
  return fetchAdminWithAuth('/agencies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAgency(agencyId: string): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}`);
}

export async function updateAgency(agencyId: string, updates: any): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Agency Localization API
export interface LocalizationOptions {
  countries: Record<string, { code: string; name: string; currency: string; defaultLanguage: string }>;
  currencies: Record<string, { code: string; symbol: string; name: string }>;
  languages: Record<string, { code: string; name: string; nativeName: string }>;
}

export async function updateAgencyLocalization(
  agencyId: string,
  localization: {
    country?: string;
    currency?: string;
    language?: string;
  }
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/localization`, {
    method: 'PUT',
    body: JSON.stringify(localization),
  });
}

export async function getLocalizationOptions(agencyId: string): Promise<LocalizationOptions> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/localization/options`);
}

// Agency Businesses API
export async function listAgencyBusinesses(agencyId: string): Promise<any[]> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses`);
  return data.businesses;
}

export async function createAgencyBusiness(
  agencyId: string,
  business: {
    name: string;
    phoneNumber: string;
    timezone: string;
    businessHours?: any;
    services?: any[];
    greeting?: string;
    transferNumber?: string;
    ownerEmail?: string;
  }
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses`, {
    method: 'POST',
    body: JSON.stringify(business),
  });
}

export async function updateAgencyBusiness(
  agencyId: string,
  businessId: string,
  updates: any
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAgencyBusiness(
  agencyId: string,
  businessId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}`, {
    method: 'DELETE',
  });
}

// Agency Members API
export async function listAgencyMembers(agencyId: string): Promise<any[]> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/members`);
  return data.members;
}

export async function inviteAgencyMember(
  agencyId: string,
  email: string,
  role: 'admin' | 'viewer',
  name?: string
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/members`, {
    method: 'POST',
    body: JSON.stringify({ email, role, name }),
  });
}

export async function removeAgencyMember(agencyId: string, memberId: string): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// Agency Analytics API
export async function getAgencyAnalytics(
  agencyId: string,
  startDate?: string,
  endDate?: string
): Promise<any> {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return fetchAdminWithAuth(`/agencies/${agencyId}/analytics?${params.toString()}`);
}

// ==================== Agency Usage API ====================

export interface UsageAggregate {
  id?: string;
  agencyId: string;
  businessId: string | null;
  businessName?: string;
  billingPeriod: string;
  totals: {
    // Retell AI voice call metrics
    retellCallCount?: number;
    retellMinutes?: number;
    retellCostCents?: number;
    totalCostCents: number;
  };
  eventCount: number;
}

export interface UsageEvent {
  id: string;
  agencyId: string;
  businessId: string;
  eventType: string;
  quantity: number;
  unit: string;
  totalCostCents: number;
  timestamp: any;
  billingPeriod: string;
  metadata?: Record<string, any>;
}

// Get agency usage summary
export async function getAgencyUsage(
  agencyId: string,
  billingPeriod?: string
): Promise<UsageAggregate> {
  const params = new URLSearchParams();
  if (billingPeriod) params.set('billingPeriod', billingPeriod);
  return fetchAdminWithAuth(`/agencies/${agencyId}/usage?${params.toString()}`);
}

// Get per-business usage breakdown
export async function getAgencyBusinessUsages(
  agencyId: string,
  billingPeriod?: string
): Promise<UsageAggregate[]> {
  const params = new URLSearchParams();
  if (billingPeriod) params.set('billingPeriod', billingPeriod);
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/usage/businesses?${params.toString()}`);
  return data.usages;
}

// Get usage events history
export async function getAgencyUsageEvents(
  agencyId: string,
  options: {
    businessId?: string;
    billingPeriod?: string;
    eventType?: string;
    limit?: number;
  } = {}
): Promise<{ events: UsageEvent[]; hasMore: boolean }> {
  const params = new URLSearchParams();
  if (options.businessId) params.set('businessId', options.businessId);
  if (options.billingPeriod) params.set('billingPeriod', options.billingPeriod);
  if (options.eventType) params.set('eventType', options.eventType);
  if (options.limit) params.set('limit', options.limit.toString());
  return fetchAdminWithAuth(`/agencies/${agencyId}/usage/events?${params.toString()}`);
}

// ==================== Phone Number API ====================

export interface AvailablePhoneNumber {
  phoneNumber: string;
  friendlyName: string;
  locality?: string;
  region?: string;
  postalCode?: string;
  isoCountry: string;
  capabilities: {
    voice: boolean;
    sms: boolean;
    mms: boolean;
  };
  type: 'local' | 'tollFree';
  monthlyPriceCents: number;
}

export interface PurchasedPhoneNumber {
  id: string;
  phoneNumber: string;
  twilioSid: string;
  agencyId: string;
  businessId: string | null;
  businessName?: string;
  type: 'local' | 'tollFree';
  monthlyPriceCents: number;
  status: 'active' | 'released';
  purchasedAt: any;
}

export interface PhoneNumberPricing {
  local: {
    monthlyPriceCents: number;
    description: string;
  };
  tollFree: {
    monthlyPriceCents: number;
    description: string;
  };
}

// Search available phone numbers
export async function searchPhoneNumbers(
  agencyId: string,
  options: {
    areaCode?: string;
    contains?: string;
    country?: string;
    type?: 'local' | 'tollFree';
    limit?: number;
  } = {}
): Promise<{ numbers: AvailablePhoneNumber[]; pricing: any }> {
  const params = new URLSearchParams();
  if (options.areaCode) params.set('areaCode', options.areaCode);
  if (options.contains) params.set('contains', options.contains);
  if (options.country) params.set('country', options.country);
  if (options.type) params.set('type', options.type);
  if (options.limit) params.set('limit', options.limit.toString());
  return fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers/search?${params.toString()}`);
}

// Get phone number pricing
export async function getPhoneNumberPricing(agencyId: string): Promise<PhoneNumberPricing> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers/pricing`);
}

// List agency's purchased phone numbers
export async function listPhoneNumbers(agencyId: string): Promise<PurchasedPhoneNumber[]> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers`);
  return data.phoneNumbers;
}

// Purchase a phone number
export async function purchasePhoneNumber(
  agencyId: string,
  phoneNumber: string,
  businessId: string,
  type: 'local' | 'tollFree' = 'local'
): Promise<PurchasedPhoneNumber> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, businessId, type }),
  });
}

// Get business's phone number
export async function getBusinessPhoneNumber(
  agencyId: string,
  businessId: string
): Promise<PurchasedPhoneNumber | null> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/phone-number`);
  return data.phoneNumber;
}

// Release a phone number
export async function releasePhoneNumber(
  agencyId: string,
  phoneNumberId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers/${phoneNumberId}`, {
    method: 'DELETE',
  });
}

// Reassign phone number to different business
export async function reassignPhoneNumber(
  agencyId: string,
  phoneNumberId: string,
  newBusinessId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers/${phoneNumberId}/reassign`, {
    method: 'PUT',
    body: JSON.stringify({ newBusinessId }),
  });
}

// Import an existing Twilio phone number
export async function importExistingPhoneNumber(
  agencyId: string,
  phoneNumber: string,
  businessId: string,
  type: 'local' | 'tollFree' = 'local'
): Promise<PurchasedPhoneNumber> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/phone-numbers/import`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, businessId, type }),
  });
}

// ==================== Business Suspension API ====================

// Suspend a business
export async function suspendBusiness(
  agencyId: string,
  businessId: string,
  reason?: string
): Promise<Business> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// Unsuspend a business
export async function unsuspendBusiness(
  agencyId: string,
  businessId: string
): Promise<Business> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/unsuspend`, {
    method: 'POST',
  });
}

// Enhanced delete business with phone number options
export type PhoneNumberAction = 'release' | 'keep' | 'reassign';

export interface DeleteBusinessResult {
  success: boolean;
  message: string;
  phoneNumberHandled: string;
}

export async function deleteBusinessWithOptions(
  agencyId: string,
  businessId: string,
  phoneNumberAction?: PhoneNumberAction,
  reassignToBusinessId?: string
): Promise<DeleteBusinessResult> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/delete`, {
    method: 'POST',
    body: JSON.stringify({ phoneNumberAction, reassignToBusinessId }),
  });
}

// ==================== Agency Business Calls API ====================

// List calls for a business (via admin API)
export async function listAgencyBusinessCalls(
  agencyId: string,
  businessId: string,
  limit?: number,
  startAfter?: string
): Promise<any[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (startAfter) params.set('startAfter', startAfter);
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calls?${params.toString()}`);
  return data.calls;
}

// Get call details (via admin API)
export async function getAgencyBusinessCall(
  agencyId: string,
  businessId: string,
  callId: string
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calls/${callId}`);
}

// ==================== Agency Business Leads API ====================

// List leads for a business (via admin API)
export async function listAgencyBusinessLeads(
  agencyId: string,
  businessId: string,
  status?: string,
  limit?: number
): Promise<any[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit) params.set('limit', limit.toString());
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/leads?${params.toString()}`);
  return data.leads;
}

// Update lead (via admin API)
export async function updateAgencyBusinessLead(
  agencyId: string,
  businessId: string,
  leadId: string,
  updates: any
): Promise<any> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// ==================== Agency Business Appointments API ====================

// List appointments for a business (via admin API)
export async function listAgencyBusinessAppointments(
  agencyId: string,
  businessId: string,
  options: { startDate?: string; endDate?: string; status?: string } = {}
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  if (options.status) params.set('status', options.status);
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/appointments?${params.toString()}`);
  return data.appointments;
}

// ==================== Agency Business Members API ====================

// List business members (via admin API)
export async function listAgencyBusinessMembers(
  agencyId: string,
  businessId: string
): Promise<BusinessMember[]> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/members`);
  return data.members;
}

// Add business member (via admin API)
export async function addAgencyBusinessMember(
  agencyId: string,
  businessId: string,
  email: string,
  role: string,
  name?: string
): Promise<BusinessMember> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/members`, {
    method: 'POST',
    body: JSON.stringify({ email, role, name }),
  });
}

// Remove business member (via admin API)
export async function removeAgencyBusinessMember(
  agencyId: string,
  businessId: string,
  memberId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// ==================== Agency Business Calendar API ====================

export interface BlockedTime {
  id: string;
  businessId: string;
  startTime: any;
  endTime: any;
  reason?: string;
  allDay?: boolean;
  recurring?: boolean;
  createdAt: any;
}

// Get available calendar slots for a business
export async function getAgencyBusinessCalendarSlots(
  agencyId: string,
  businessId: string,
  date: string,
  duration?: number
): Promise<CalendarSlot[]> {
  const params = new URLSearchParams();
  params.set('date', date);
  if (duration) params.set('duration', duration.toString());
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calendar/slots?${params.toString()}`);
  return data.slots;
}

// Get blocked times for a business
export async function getAgencyBusinessBlockedTimes(
  agencyId: string,
  businessId: string,
  startDate: string,
  endDate: string
): Promise<BlockedTime[]> {
  const params = new URLSearchParams();
  params.set('startDate', startDate);
  params.set('endDate', endDate);
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calendar/blocked-times?${params.toString()}`);
  return data.blockedTimes;
}

// Create a blocked time entry
export async function createAgencyBusinessBlockedTime(
  agencyId: string,
  businessId: string,
  data: {
    startTime: string;
    endTime: string;
    reason?: string;
    allDay?: boolean;
    recurring?: boolean;
  }
): Promise<{ blockedTimeId: string }> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calendar/blocked-times`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Delete a blocked time entry
export async function deleteAgencyBusinessBlockedTime(
  agencyId: string,
  businessId: string,
  blockedTimeId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/calendar/blocked-times/${blockedTimeId}`, {
    method: 'DELETE',
  });
}

// ==================== Super Admin API ====================

const SUPERADMIN_API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || '/api'}/superadmin`;

async function fetchSuperAdminWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${SUPERADMIN_API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Get platform-wide usage summary
export async function getPlatformUsage(billingPeriod?: string): Promise<{
  billingPeriod: string;
  totalAgencies: number;
  overallTotals: UsageAggregate['totals'];
  agencyUsages: UsageAggregate[];
}> {
  const params = new URLSearchParams();
  if (billingPeriod) params.set('billingPeriod', billingPeriod);
  return fetchSuperAdminWithAuth(`/usage?${params.toString()}`);
}

// Get all agencies (super admin)
export async function getAllAgencies(): Promise<{
  agencies: any[];
  totalBusinesses: number;
}> {
  return fetchSuperAdminWithAuth('/agencies');
}

// Get specific agency usage (super admin)
export async function getSuperAdminAgencyUsage(
  agencyId: string,
  billingPeriod?: string
): Promise<{
  agency: any;
  usage: UsageAggregate;
  businessUsages: UsageAggregate[];
}> {
  const params = new URLSearchParams();
  if (billingPeriod) params.set('billingPeriod', billingPeriod);
  return fetchSuperAdminWithAuth(`/agencies/${agencyId}/usage?${params.toString()}`);
}

// Set super admin status for a user
export async function setSuperAdmin(userId: string, isSuperAdmin: boolean): Promise<void> {
  await fetchSuperAdminWithAuth(`/users/${userId}/set-superadmin`, {
    method: 'POST',
    body: JSON.stringify({ isSuperAdmin }),
  });
}

// ==================== Retell AI API ====================

export interface RetellVoice {
  voice_id: string;
  voice_name: string;
  provider: string;
  accent?: string;
  gender?: string;
  age?: string;
  preview_audio_url?: string;
}

export interface RetellAgentStatus {
  hasAgent: boolean;
  agentId?: string;
  llmId?: string;
  agentName?: string;
  voiceId?: string;
  hasPhoneNumber: boolean;
  phoneNumber?: string;
  phoneNumberId?: string;
}

export interface RetellPhoneNumber {
  phone_number: string;
  phone_number_id: string;
  nickname?: string;
  inbound_agent_id?: string;
}

// List available Retell voices (optionally filtered by language)
export async function listRetellVoices(agencyId: string, language?: string): Promise<RetellVoice[]> {
  const params = language ? `?language=${language}` : '';
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/retell/voices${params}`);
  return data.voices;
}

// Set up Retell agent for a business
export async function setupRetellAgent(
  agencyId: string,
  businessId: string,
  voiceId?: string
): Promise<{ agentId: string; llmId: string }> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell/setup`, {
    method: 'POST',
    body: JSON.stringify({ voiceId }),
  });
}

// Get Retell agent status for a business
export async function getRetellStatus(
  agencyId: string,
  businessId: string
): Promise<RetellAgentStatus> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell`);
}

// Sync Retell agent configuration with business settings
export async function syncRetellAgent(
  agencyId: string,
  businessId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell/sync`, {
    method: 'PUT',
  });
}

// Delete Retell agent for a business
export async function deleteRetellAgent(
  agencyId: string,
  businessId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell`, {
    method: 'DELETE',
  });
}

// Purchase a Retell phone number for a business
export async function purchaseRetellPhoneNumber(
  agencyId: string,
  businessId: string,
  areaCode?: string,
  nickname?: string
): Promise<{ phoneNumber: string; phoneNumberId: string }> {
  return fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell/purchase-phone`, {
    method: 'POST',
    body: JSON.stringify({ areaCode, nickname }),
  });
}

// Release/delete a Retell phone number
export async function releaseRetellPhoneNumber(
  agencyId: string,
  businessId: string
): Promise<void> {
  await fetchAdminWithAuth(`/agencies/${agencyId}/businesses/${businessId}/retell/phone`, {
    method: 'DELETE',
  });
}

// List all Retell phone numbers for an agency
export async function listRetellPhoneNumbers(agencyId: string): Promise<RetellPhoneNumber[]> {
  const data = await fetchAdminWithAuth(`/agencies/${agencyId}/retell/phone-numbers`);
  return data.phoneNumbers;
}

// ==================== Billing API ====================

const BILLING_API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || '/api'}/billing`;

async function fetchBillingWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${BILLING_API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

export interface BillingPlan {
  id: string;
  name: string;
  priceId: string;
  priceCents: number;
  maxBusinesses: number;
  features: string[];
}

export interface SubscriptionInfo {
  plan: 'starter' | 'professional' | 'enterprise';
  status: string;
  currentPeriodEnd: string | null;
  features: string[];
  priceCents: number;
}

// Get available subscription plans
export async function getSubscriptionPlans(): Promise<BillingPlan[]> {
  const data = await fetchBillingWithAuth('/plans');
  return data.plans;
}

// Get agency subscription status
export async function getAgencySubscription(agencyId: string): Promise<SubscriptionInfo> {
  return fetchBillingWithAuth(`/agencies/${agencyId}/subscription`);
}

// Create checkout session to subscribe (redirect mode - legacy)
export async function createCheckoutSession(
  agencyId: string,
  plan: string,
  successUrl: string,
  cancelUrl: string
): Promise<{ url: string }> {
  return fetchBillingWithAuth(`/agencies/${agencyId}/checkout`, {
    method: 'POST',
    body: JSON.stringify({ plan, successUrl, cancelUrl }),
  });
}

// Create embedded checkout session (stays on platform)
export async function createEmbeddedCheckoutSession(
  agencyId: string,
  plan: string,
  returnUrl: string
): Promise<{ clientSecret: string; sessionId: string }> {
  return fetchBillingWithAuth(`/agencies/${agencyId}/embedded-checkout`, {
    method: 'POST',
    body: JSON.stringify({ plan, returnUrl }),
  });
}

// Get checkout session status (for verifying completion)
export async function getCheckoutSessionStatus(
  sessionId: string
): Promise<{ status: string; customerEmail?: string }> {
  return fetchBillingWithAuth(`/session/${sessionId}/status`);
}

// Create billing portal session for managing subscription
export async function createBillingPortalSession(
  agencyId: string,
  returnUrl: string
): Promise<{ url: string }> {
  return fetchBillingWithAuth(`/agencies/${agencyId}/portal`, {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

// Cancel subscription
export async function cancelSubscription(agencyId: string): Promise<{ success: boolean; message: string }> {
  return fetchBillingWithAuth(`/agencies/${agencyId}/cancel`, {
    method: 'POST',
  });
}

// ==================== Stripe Connect API ====================

const CONNECT_API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || '/api'}/connect`;

async function fetchConnectWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${CONNECT_API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

export interface ConnectStatus {
  accountId: string;
  onboardingComplete: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
}

// Get Stripe Connect status for agency
export async function getConnectStatus(agencyId: string): Promise<{ status: ConnectStatus | null }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/status`);
}

// Create Stripe Connect account for agency
export async function createConnectAccount(agencyId: string): Promise<{ accountId: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/account`, {
    method: 'POST',
  });
}

// Get onboarding link to set up Stripe Connect
export async function getConnectOnboardingLink(
  agencyId: string,
  refreshUrl: string,
  returnUrl: string
): Promise<{ url: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/onboarding-link`, {
    method: 'POST',
    body: JSON.stringify({ refreshUrl, returnUrl }),
  });
}

// Get Stripe dashboard login link
export async function getConnectLoginLink(agencyId: string): Promise<{ url: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/login-link`, {
    method: 'POST',
  });
}

// Create checkout session for client payment (redirect mode - legacy)
export async function createClientCheckout(
  agencyId: string,
  businessId: string,
  amountCents: number,
  description: string,
  successUrl: string,
  cancelUrl: string
): Promise<{ url: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/clients/${businessId}/checkout`, {
    method: 'POST',
    body: JSON.stringify({ amountCents, description, successUrl, cancelUrl }),
  });
}

// Create embedded checkout session for client payment
export async function createClientEmbeddedCheckout(
  agencyId: string,
  businessId: string,
  amountCents: number,
  description: string,
  returnUrl: string
): Promise<{ clientSecret: string; sessionId: string; connectedAccountId: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/clients/${businessId}/embedded-checkout`, {
    method: 'POST',
    body: JSON.stringify({ amountCents, description, returnUrl }),
  });
}

// Create subscription for client (redirect mode - legacy)
export async function createClientSubscription(
  agencyId: string,
  businessId: string,
  priceAmountCents: number,
  interval: 'month' | 'year',
  successUrl: string,
  cancelUrl: string
): Promise<{ url: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/clients/${businessId}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ priceAmountCents, interval, successUrl, cancelUrl }),
  });
}

// Create embedded subscription for client
export async function createClientEmbeddedSubscription(
  agencyId: string,
  businessId: string,
  priceAmountCents: number,
  interval: 'month' | 'year',
  returnUrl: string
): Promise<{ clientSecret: string; sessionId: string; connectedAccountId: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/clients/${businessId}/embedded-subscription`, {
    method: 'POST',
    body: JSON.stringify({ priceAmountCents, interval, returnUrl }),
  });
}

// Get Connect checkout session status
export async function getConnectCheckoutSessionStatus(
  agencyId: string,
  sessionId: string
): Promise<{ status: string; customerEmail?: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/session/${sessionId}/status`);
}

// Get client payments
export async function getClientPayments(agencyId: string, limit?: number): Promise<{ payments: any[] }> {
  const url = limit ? `/agencies/${agencyId}/payments?limit=${limit}` : `/agencies/${agencyId}/payments`;
  return fetchConnectWithAuth(url);
}

// Get client subscriptions
export async function getClientSubscriptions(agencyId: string, limit?: number): Promise<{ subscriptions: any[] }> {
  const url = limit ? `/agencies/${agencyId}/subscriptions?limit=${limit}` : `/agencies/${agencyId}/subscriptions`;
  return fetchConnectWithAuth(url);
}

// Create an invoice for a client (business)
export async function createClientInvoice(
  agencyId: string,
  businessId: string,
  data: {
    type: 'one_time' | 'subscription';
    amountCents: number;
    description: string;
    interval?: 'month' | 'year';
    dueDate?: string;
  }
): Promise<{ invoiceId: string }> {
  return fetchConnectWithAuth(`/agencies/${agencyId}/clients/${businessId}/invoice`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
